import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, TrendingUp, BookOpen, Clock, Star, Calendar, AlertCircle, Building, User, FileText, Globe, ChevronRight, ChevronLeft, Eye, Play, AwardIcon, Filter, Brain, Upload, Inbox, Database, Move, MoveRight, ArrowRight, Replace, Move3D, Move3DIcon, Loader2, Sparkles, PlusCircle, Lightbulb, Wand2, Target, Zap, Briefcase, PlayCircle, PlayCircleIcon, PlusIcon, Settings2, HistoryIcon, ParenthesesIcon, Presentation, TestTubeDiagonalIcon, TestTube2, TestTube2Icon, TestTubeIcon, X  } from 'lucide-react';
import { Button } from '../common/Button';
import { Interview } from '../../types/interviews';
import { ScheduleInterviewModal } from '../interviews/ScheduleInterviewModal';
import { useNavigate } from 'react-router-dom';
import configService, { JobRole, Company, Language } from '../../services/configService';
import interviewService, { SeriesDataResponse, UserInterview } from '../../services/interviewService';
import { RecommendedItem } from '../../types/recommendations';
import { useAuthStore } from '../../store/authStore';
import { Newspaper } from 'lucide-react';
import CustomInterviewPaywallModal from '../interviews/CustomInterviewPaywallModal';
import { useToast } from '../common/Toast';
import { DurationDisplay } from '../common/DurationDisplay';
import CompanyLogo from '../common/CompanyLogo';

// Tab types
type InterviewTab = 'recommended' | 'series' | 'practice' | 'feed';

interface PrepareForInterviewProps {
  // Callback function to handle recommendation load, will be used to trigger the First time Recommended Interview Modal
  onRecommendationLoad?: (recommendation: RecommendedItem) => void;
  // Callback function to refresh upcoming interviews in the sidebar
  onRefreshUpcomingInterviews?: () => void;
  // Callback function to refresh custom interviews and switch to practice tab
  onRefreshCustomInterviews?: () => void;
}

// Desktop Horizontal Progress Flow Component
const HorizontalProgressFlow = () => {
  const steps = [
    { icon: <FileText className="w-4 h-4" />, text: "Analyze Resume", animationClass: "animate-step-activate-blue" },
    { icon: <Brain className="w-4 h-4" />, text: "AI Processing", animationClass: "animate-step-activate-purple" },
    { icon: <Globe className="w-4 h-4" />, text: "Curate News", animationClass: "animate-step-activate-green" },
    { icon: <Target className="w-4 h-4" />, text: "Deliver Results", animationClass: "animate-step-activate-orange" }
  ];

  return (
    <div className="flex items-center justify-between w-full">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center text-center relative z-10">
            <div 
              className={`p-2 rounded-full mb-2 bg-gray-400 dark:bg-gray-600 ${step.animationClass}`}
              style={{
                animationDelay: `${index * 1.5}s`,
                animationFillMode: 'both'
              }}
            >
              <div className="text-white">
                {step.icon}
              </div>
            </div>
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark font-medium">
              {step.text}
            </span>
          </div>
          
          {/* Horizontal line after each circle except the last */}
          {index < 3 && (
            <div className="flex-1 relative flex items-center" style={{ marginTop: '-12px' }}>
              <div className="w-full h-0.5 bg-gray-300 dark:bg-gray-600"></div>
              <div 
                className="absolute top-0 left-0 h-0.5 bg-primary-light dark:bg-primary-dark w-0 animate-line-progress"
                style={{
                  animationDelay: `${index * 1.5 + 0.5}s`,
                  animationFillMode: 'both'
                }}
              ></div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Mobile Vertical Progress Flow Component
const VerticalProgressFlow = () => {
  const steps = [
    { icon: <FileText className="w-4 h-4" />, text: "Analyze Resume", animationClass: "animate-step-activate-blue" },
    { icon: <Brain className="w-4 h-4" />, text: "AI Processing", animationClass: "animate-step-activate-purple" },
    { icon: <Globe className="w-4 h-4" />, text: "Curate News", animationClass: "animate-step-activate-green" },
    { icon: <Target className="w-4 h-4" />, text: "Deliver Results", animationClass: "animate-step-activate-orange" }
  ];

  return (
    <div className="flex flex-col items-center gap-2">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center text-center relative z-10">
            <div 
              className={`p-2 rounded-full mb-2 bg-gray-400 dark:bg-gray-600 ${step.animationClass}`}
              style={{
                animationDelay: `${index * 1.5}s`,
                animationFillMode: 'both'
              }}
            >
              <div className="text-white">
                {step.icon}
              </div>
            </div>
            <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark font-medium">
              {step.text}
            </span>
          </div>
          
          {/* Vertical line after each circle except the last */}
          {index < 3 && (
            <div className="relative flex flex-col items-center" style={{ marginTop: '8px', marginBottom: '8px' }}>
              <div className="w-0.5 h-8 bg-gray-300 dark:bg-gray-600"></div>
              <div 
                className="absolute top-0 left-0 w-0.5 bg-primary-light dark:bg-primary-dark h-0 animate-line-progress-vertical"
                style={{
                  animationDelay: `${index * 1.5 + 0.5}s`,
                  animationFillMode: 'both'
                }}
              ></div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export const PrepareForInterview: React.FC<PrepareForInterviewProps> = ({ onRecommendationLoad, onRefreshUpcomingInterviews, onRefreshCustomInterviews }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<InterviewTab>('recommended');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [interviewSeries, setInterviewSeries] = useState<SeriesDataResponse[]>([]);
  const [filteredSeries, setFilteredSeries] = useState<SeriesDataResponse[]>([]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // NEW: Flag to track if onboarding callback has been triggered to prevent continuous calls
  const [hasTriggeredOnboardingCallback, setHasTriggeredOnboardingCallback] = useState(false);

  // NEW: Ref to track if recommendations have been loaded to prevent multiple API calls
  const hasLoadedRecommendations = useRef(false);
  const isInitialLoad = useRef(true);

  // NEW: Reset callback flag when localStorage flags are cleared
  useEffect(() => {
    const checkOnboardingFlags = () => {
      const isFromOnboarding = localStorage.getItem('from-onboarding') === 'true';
      const previousPath = localStorage.getItem('previous-path');
      const hasOnboardingFlags = isFromOnboarding || previousPath?.includes('/onboarding/subscription') || previousPath?.includes('/setup-profile');
      
      // If no onboarding flags are present, reset the callback flag
      if (!hasOnboardingFlags && hasTriggeredOnboardingCallback) {
        console.log('PrepareForInterview: Resetting onboarding callback flag - no onboarding flags present');
        setHasTriggeredOnboardingCallback(false);
      }
    };
    
    // Check immediately and then periodically
    checkOnboardingFlags();
    const interval = setInterval(checkOnboardingFlags, 1000); // Check every second
    
    return () => clearInterval(interval);
  }, [hasTriggeredOnboardingCallback]);

  // Form state for Practice tab
  const [jobRole, setJobRole] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobDescription, setJobDescription] = useState(''); // This will be the interview round type
  const [customRoundType, setCustomRoundType] = useState(''); // For custom interview round type
  const [interviewLanguage, setInterviewLanguage] = useState('English');
  const [actualJobDescription, setActualJobDescription] = useState(''); // The actual job description text
  const [interviewDifficulty, setInterviewDifficulty] = useState(3); // Default to Medium (3)
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>({
    id: '',
    title: '',
    company: '',
    jobRole: '',
    duration: 30,
    description: '',
    language: 'English',
    score: 0,
    date: new Date().toISOString(),
  });

  // Custom interviews state
  const [customInterviews, setCustomInterviews] = useState<UserInterview[]>([]);
  const [showCustomInterviewsList, setShowCustomInterviewsList] = useState(false);
  const [isCreatingCustomInterview, setIsCreatingCustomInterview] = useState(false);
  const [showCustomInterviewForm, setShowCustomInterviewForm] = useState(false);
  const [customInterviewsLoaded, setCustomInterviewsLoaded] = useState(false);
  
  // Paywall modal state
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [usageDetails, setUsageDetails] = useState<{
    currentMonthCount: number;
    monthlyLimit: number;
    resetDate: string;
    userPlan: string;
    canCreateMore: boolean;
    isFreePlan: boolean;
  } | null>(null);
  
  // Pagination state for custom interviews
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Pagination state for recommended interviews
  const [currentRecommendedPage, setCurrentRecommendedPage] = useState(1);
  const recommendedItemsPerPage = 7;

  // Pagination state for series
  const [currentSeriesPage, setCurrentSeriesPage] = useState(1);
  const seriesItemsPerPage = 7;

  // Smart pagination function to generate page numbers with ellipsis
  const generatePageNumbers = (currentPage: number, totalPages: number) => {
    const pages = [];
    const maxVisiblePages = isMobileView ? 3 : 5; // Show fewer pages on mobile
    
    if (totalPages <= maxVisiblePages + 2) {
      // If total pages is small, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage, endPage;
      
      if (currentPage <= 3) {
        // If current page is near the beginning
        startPage = 2;
        endPage = Math.min(maxVisiblePages, totalPages - 1);
      } else if (currentPage >= totalPages - 2) {
        // If current page is near the end
        startPage = Math.max(totalPages - maxVisiblePages + 1, 2);
        endPage = totalPages - 1;
      } else {
        // If current page is in the middle
        const around = Math.floor((maxVisiblePages - 1) / 2);
        startPage = Math.max(currentPage - around, 2);
        endPage = Math.min(currentPage + around, totalPages - 1);
      }
      
      // Add ellipsis before start if needed
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis after end if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // AI form generation state
  const [aiDescription, setAiDescription] = useState('');
  const [isGeneratingFormData, setIsGeneratingFormData] = useState(false);

  // Data fetched from API
  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [recommendedInterviews, setRecommendedInterviews] = useState<RecommendedItem[]>([]);
  const [filteredRecommendedInterviews, setFilteredRecommendedInterviews] = useState<RecommendedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(true);
  const [isLoadingSeries, setIsLoadingSeries] = useState(true);

  // Async recommendation polling state
  const [recommendationJobId, setRecommendationJobId] = useState<string | null>(null);
  const [recommendationJobStatus, setRecommendationJobStatus] = useState<'pending' | 'success' | 'failed' | null>(null);
  const [recommendationJobError, setRecommendationJobError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // State for scheduling interviews from recommended cards
  const [selectedInterviewForScheduling, setSelectedInterviewForScheduling] = useState<RecommendedItem | null>(null);
  const [showScheduleModalForRecommended, setShowScheduleModalForRecommended] = useState(false);

  // Shorter tab labels for mobile
  const tabLabels = {
    recommended: isMobileView ? 'For You' : 'Recommended For You',
    series: isMobileView ? 'Series' : 'Browse All Series',
    feed: isMobileView ? 'News' : 'News For You',
    practice: isMobileView ? 'Custom' : 'Custom Interview'
  };

  // Check mobile view with improved detection based on Navbar.tsx approach
  useEffect(() => {
    const checkMobileView = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;
      setIsMobileView(isMobile);
    };

    // Initialize on mount
    checkMobileView();

    // Set up ResizeObserver for more reliable detection
    const bodyResizeObserver = new ResizeObserver(() => {
      // We observe the body to detect viewport size changes
      checkMobileView();
    });

    bodyResizeObserver.observe(document.body);

    // Also listen for traditional events as fallback
    window.addEventListener('resize', checkMobileView);

    // Force a re-check after a short delay to handle some edge cases
    setTimeout(checkMobileView, 300);

    return () => {
      window.removeEventListener('resize', checkMobileView);
      bodyResizeObserver.disconnect();
    };
  }, []);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch all data in parallel
        const [fetchedJobRoles, fetchedCompanies, fetchedLanguages] = await Promise.all([
          configService.getJobRoles(),
          configService.getCompanies(),
          configService.getLanguages()
        ]);

        setJobRoles(fetchedJobRoles);
        setCompanies(fetchedCompanies);
        setLanguages(fetchedLanguages);

        // Set default values if arrays are not empty
        if (fetchedJobRoles.length > 0 && !jobRole) {
          setJobRole('');
        }

        if (fetchedCompanies.length > 0 && !companyName) {
          setCompanyName('');
        }

        if (fetchedLanguages.length > 0 && !interviewLanguage) {
          setInterviewLanguage(fetchedLanguages[0].name);
        }
      } catch (error) {
        console.error('Error fetching data for interview form:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [jobRole, companyName, interviewLanguage]);

  // Track preference changes to force refresh recommendations when needed
  const [careerPrefsVersion, setCareerPrefsVersion] = useState<number>(0);

  // Function to trigger a refresh of recommendations
  const refreshRecommendations = () => {
    setCareerPrefsVersion(prev => prev + 1); // Increment to trigger the useEffect
  };

  // Listen for career preference changes using custom event
  useEffect(() => {
    const handlePrefsChanged = () => {
      refreshRecommendations();
    };

    // Add event listener for career preference changes
    window.addEventListener('career-preferences-changed', handlePrefsChanged);

    return () => {
      window.removeEventListener('career-preferences-changed', handlePrefsChanged);
    };
  }, []);

  // Fetch recommended interviews
  useEffect(() => {
    const fetchRecommendedInterviews = async () => {
      if (!user?._id) {
        setIsLoadingRecommendations(false);
        setRecommendedInterviews([]);
        return;
      }
      setIsLoadingRecommendations(true);
      try {
        // Force a refresh when careerPrefsVersion changes
        const forceRefresh = careerPrefsVersion > 0;

        // Pass userId in the query and forceRefresh when needed
        const apiResponse = await interviewService.getRecommendedInterviews({
          userId: user._id,
          forceRefresh
        });

        if (apiResponse && apiResponse.data && Array.isArray(apiResponse.data)) {
          const interviewsData = apiResponse.data; // This is RecommendedItem[]
          if (interviewsData.length > 0) {
            // FIXED: Only trigger callback for onboarding users and only once
            if (onRecommendationLoad && !hasTriggeredOnboardingCallback) {
              const isFromOnboarding = localStorage.getItem('from-onboarding') === 'true';
              const previousPath = localStorage.getItem('previous-path');
              
              if (isFromOnboarding || (previousPath?.includes('/onboarding/subscription') || previousPath?.includes('/setup-profile'))) {
                console.log('PrepareForInterview: Triggering onboarding callback for first time');
                // Randomly choose a recommendation (or first one)
                const randomIndex = Math.floor(Math.random() * interviewsData.length);
                onRecommendationLoad(interviewsData[randomIndex]);
                setHasTriggeredOnboardingCallback(true);
              }
            }
            setRecommendedInterviews(interviewsData);
            setFilteredRecommendedInterviews(interviewsData);
          } else {
            setRecommendedInterviews([]);
            setFilteredRecommendedInterviews([]);
          }
        } else {
          console.error('Invalid API response structure for recommended interviews:', apiResponse);
          setRecommendedInterviews([]);
          setFilteredRecommendedInterviews([]);
        }
      } catch (error) {
        console.error('Error fetching recommended interviews:', error);
        setRecommendedInterviews([]);
        setFilteredRecommendedInterviews([]);
      } finally {
        setIsLoadingRecommendations(false);
      }
    };

    fetchRecommendedInterviews();
  }, [onRecommendationLoad, user?._id, careerPrefsVersion, hasTriggeredOnboardingCallback]);

  // Fetch interview series from the API
  useEffect(() => {
    const fetchInterviewSeries = async () => {
      setIsLoadingSeries(true);
      try {
        // Force refresh if careerPrefsVersion has changed
        const forceRefresh = careerPrefsVersion > 0;

        const series = await interviewService.getInterviewSeriesList(forceRefresh);

        if (series && series.length > 0) {

          // Clean up the series data before setting state
          const cleanedSeries = series.map(s => ({
            ...s,
            // Ensure all required properties exist
            _id: s._id || '',
            name: s.name || 'Untitled Series',
            description: s.description || '',
            company: s.company || '',
            role: s.role || '',
            difficulty: s.difficulty || 0,
            totalTimeMinutes: s.totalTimeMinutes || 0,
            // Normalize match score to ensure it exists
            matchScore: s.matchScore || s.match_score || 0,
            match_score: s.match_score || s.matchScore || 0,
          }));

          setInterviewSeries(cleanedSeries);
          setFilteredSeries(cleanedSeries);
        } else {
          setInterviewSeries([]);
          setFilteredSeries([]);
        }
      } catch (error) {
        console.error('Error fetching interview series:', error);
        setInterviewSeries([]);
        setFilteredSeries([]);
      } finally {
        setIsLoadingSeries(false);
      }
    };

    fetchInterviewSeries();
  }, []);

  // Backward compatibility - Replaced the frontend filtering with API-based search
  const handleSearchQueryChange = async (query: string) => {
    setSearchQuery(query);
    
    // Reset pagination when search query changes
    setCurrentRecommendedPage(1);
    setCurrentSeriesPage(1);

    // Handle different search behavior based on active tab
    if (activeTab === 'series') {
      // If query is empty, show all series without making an API call
      if (!query.trim()) {
        setFilteredSeries(interviewSeries);
        return;
      }

      // Show loading state
      setIsSearching(true);

      try {
        // Use backend search API for series
        console.log('Searching for series with query:', query);
        const searchResults = await interviewService.getSeriesBySearch(query);
        console.log('Series search results:', searchResults);
        setFilteredSeries(searchResults);
      } catch (error) {
        console.error('Error searching interview series:', error);
        // In case of error, fallback to existing series
        setFilteredSeries([]);
      } finally {
        setIsSearching(false);
      }
    } else if (activeTab === 'recommended') {
      // For recommended tab, filter locally instead of API call
      setIsSearching(true);

      if (!query.trim()) {
        setFilteredRecommendedInterviews(recommendedInterviews);
      } else {
        const lowercaseQuery = query.toLowerCase();

        // Filter interviews that match the search query
        const filtered = recommendedInterviews.filter(interview =>
          interview.company?.toLowerCase().includes(lowercaseQuery) ||
          interview.role?.toLowerCase().includes(lowercaseQuery) ||
          interview.metadata?.tags?.some(tag => tag.toLowerCase().includes(lowercaseQuery)) ||
          interview.description?.toLowerCase().includes(lowercaseQuery)
        );

        // Score and sort the filtered results based on relevance to the search query
        const scoredResults = filtered.map(interview => {
          let score = 0;
          const role = interview.role?.toLowerCase() || '';
          const company = interview.company?.toLowerCase() || '';
          const tags = interview.metadata?.tags || [];

          // Prioritize exact matches in role/title
          if (role === lowercaseQuery) {
            score += 100; // Exact role match gets highest score
          } else if (role.startsWith(lowercaseQuery)) {
            score += 80;  // Role starts with query
          } else if (role.includes(lowercaseQuery)) {
            score += 60;  // Role contains query
          }

          // Score company matches
          if (company === lowercaseQuery) {
            score += 50;  // Exact company match
          } else if (company.startsWith(lowercaseQuery)) {
            score += 40;  // Company starts with query
          } else if (company.includes(lowercaseQuery)) {
            score += 30;  // Company contains query
          }

          // Score tag matches
          if (tags.some(tag => tag.toLowerCase() === lowercaseQuery)) {
            score += 25;  // Exact tag match
          } else if (tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))) {
            score += 15;  // Tag contains query
          }

          // Score description matches
          if (interview.description?.toLowerCase().includes(lowercaseQuery)) {
            score += 10;  // Description contains query
          }

          return { interview, score };
        });

        // Sort by score (highest first)
        scoredResults.sort((a, b) => b.score - a.score);

        // Extract just the interviews for state update
        const sortedFiltered = scoredResults.map(item => item.interview);
        setFilteredRecommendedInterviews(sortedFiltered);


      }

      // Short timeout to show loading indicator briefly for consistency
      setTimeout(() => setIsSearching(false), 300);
    }
  };

  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      setFilteredRecommendedInterviews(recommendedInterviews);
      setFilteredSeries(interviewSeries);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const lowercaseQuery = searchQuery.toLowerCase().trim();

    // Debounce function for API search
    const debounceTimeout = setTimeout(async () => {
      try {
        // For recommendations tab, use the hybrid search API
        if (activeTab === 'recommended') {
          console.log('Using hybrid search for query:', searchQuery);
          const hybridResults = await interviewService.hybridSearchInterviews(searchQuery, user?._id);
          
          if (hybridResults && hybridResults.combinedResults) {
            console.log('Hybrid search results:', {
              textMatches: hybridResults.searchMetadata.textMatches,
              vectorMatches: hybridResults.searchMetadata.vectorMatches,
              totalResults: hybridResults.searchMetadata.totalResults
            });
            
            // Use the combined results which prioritize text matches first
            setFilteredRecommendedInterviews(hybridResults.combinedResults);
          } else {
            // Fallback to regular search if hybrid search fails
            console.log('Falling back to regular search');
            const searchResults = await interviewService.searchInterviews(searchQuery, user?._id);
            if (searchResults && searchResults.data && Array.isArray(searchResults.data)) {
              setFilteredRecommendedInterviews(searchResults.data);
            }
          }
        } else if (activeTab === 'series') {
          // Use backend search API for series
          console.log('Searching for series with query:', searchQuery);
          const searchResults = await interviewService.getSeriesBySearch(searchQuery);
          console.log('Series search results:', searchResults);
          setFilteredSeries(searchResults);
        }
      } catch (error) {
        console.error('Error during search:', error);

        // Fallback to client-side filtering in case of API error
        if (activeTab === 'recommended') {
          const filteredRecommendations = recommendedInterviews.filter(interview => {
            return (
              interview.title?.toLowerCase().includes(lowercaseQuery) ||
              interview.description?.toLowerCase().includes(lowercaseQuery) ||
              interview.company?.toLowerCase().includes(lowercaseQuery) ||
              interview.role?.toLowerCase().includes(lowercaseQuery) ||
              interview.metadata?.tags?.some((tag: string) => tag.toLowerCase().includes(lowercaseQuery)) ||
              interview.jobRoleTitle?.toLowerCase().includes(lowercaseQuery)
            );
          });
          setFilteredRecommendedInterviews(filteredRecommendations);
        } else if (activeTab === 'series') {
          // Fallback to client-side filtering for series
          const filteredSeriesList = interviewSeries.filter(series => {
            return (
              series.name?.toLowerCase().includes(lowercaseQuery) ||
              series.description?.toLowerCase().includes(lowercaseQuery) ||
              series.company?.toLowerCase().includes(lowercaseQuery) ||
              series.role?.toLowerCase().includes(lowercaseQuery) ||
              series.jobRoleTitle?.toLowerCase().includes(lowercaseQuery)
            );
          });
          setFilteredSeries(filteredSeriesList);
        }
      } finally {
        setIsSearching(false);
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimeout);
  }, [searchQuery, recommendedInterviews, interviewSeries, activeTab, user?._id]);

  // Reset search when switching tabs
  useEffect(() => {
    setSearchQuery('');
    // Reset pagination when switching tabs
    setCurrentRecommendedPage(1);
    setCurrentSeriesPage(1);
    setCurrentPage(1);
    
    if (activeTab === 'series') {
      setFilteredSeries(interviewSeries);
    } else if (activeTab === 'recommended') {
      setFilteredRecommendedInterviews(recommendedInterviews);
    }
  }, [activeTab, interviewSeries, recommendedInterviews]);

  // Handle starting practice for a recommended interview
  const handleStartPractice = useCallback((interview: RecommendedItem) => {

    // Navigate to the interview preview page
    navigate(`/interview-preview/${interview.id}`, {
      state: {
        interviewDetails: {
          id: interview.id,
          title: interview.title || interview.metadata?.title,
          company: interview.company || interview.metadata?.company,
          companyLogo: interview.metadata?.companyLogo,
          jobRole: interview.role || interview.metadata?.role,
          duration: interview.durationMinutes || interview.metadata?.durationMinutes,
          description: interview.description || interview.metadata?.description,
          tags: interview.metadata?.tags,
          status: "not_started",
          language: interview.metadata?.language || 'English',
          newInterview: true,
          voicePack: interview.metadata?.voicePack,
          characterGender: interview.metadata?.characterGender,
          interviewPrompt: interview.metadata?.interviewPrompt,
          series: undefined,
          createdAt: interview.metadata?.createdAt,
          updatedAt: interview.metadata?.updatedAt,
        }
      }
    });
  }, [navigate]);

  // Handle starting custom interview from recommendation card
  const handleStartCustomInterview = useCallback(() => {
    // Switch to practice tab and force show the form
    setActiveTab('practice');
    setShowCustomInterviewForm(true);
    setShowCustomInterviewsList(false);
  }, []);

  // Handle scheduling interview for later from recommended cards
  const handleScheduleForLater = useCallback((interview: RecommendedItem) => {
    setSelectedInterviewForScheduling(interview);
    setShowScheduleModalForRecommended(true);
  }, []);

  // Handle scheduling interview for later from custom interview cards
  const handleScheduleCustomForLater = useCallback((interview: UserInterview) => {
    // Convert UserInterview to Interview format for the modal
    // Use the custom interview template ID, not the user interview session ID
    const interviewForModal: Interview = {
      id: interview.interview?._id || '', // This is the custom interview template ID
      title: interview.interview?.title || '',
      company: interview.interview?.company || '',
      jobRole: interview.interview?.role || '',
      duration: interview.interview?.durationMinutes || 30,
      description: interview.interview?.description || '',
      language: interview.interview?.language || 'English',
      score: 0,
      date: new Date().toISOString(),
    };
    setSelectedInterview(interviewForModal);
    setShowScheduleModal(true);
  }, []);

  // Handle scheduling submission for recommended interviews
  const handleScheduleSubmit = useCallback(async (date: Date, language?: string) => {
    if (!selectedInterviewForScheduling) return;
    
    try {
      // Schedule the interview using the interview service
      await interviewService.scheduleInterview(
        selectedInterviewForScheduling.id,
        date,
        language || 'English'
      );
      
      // Close the modal
      setShowScheduleModalForRecommended(false);
      setSelectedInterviewForScheduling(null);
      
      // Show success message
      showToast('Interview scheduled successfully!', 'success');
      
      // Refresh upcoming interviews in the sidebar
      if (onRefreshUpcomingInterviews) {
        onRefreshUpcomingInterviews();
      }
    } catch (error) {
      console.error('Error scheduling interview:', error);
      showToast('Failed to schedule interview. Please try again.', 'error');
    }
  }, [selectedInterviewForScheduling, onRefreshUpcomingInterviews, showToast]);

  // Handle scheduling submission for custom interviews
  const handleScheduleCustomSubmit = useCallback(async (date: Date, language?: string) => {
    if (!selectedInterview) return;
    
    try {
      // For custom interviews, use startCustomInterviewSession instead of regular scheduleInterview
      console.log('🗓️ Scheduling custom interview for later:', selectedInterview.id);
      
      // Find the language ID if provided
      let selectedLanguageId = undefined;
      if (language && languages.length > 0) {
        const languageObj = languages.find(lang => lang.name === language);
        selectedLanguageId = languageObj?._id;
      }
      
      // Create a scheduled custom interview session
      const scheduledSession = await interviewService.startCustomInterviewSession(
        selectedInterview.id, // This is the custom interview template ID
        date,
        selectedLanguageId
      );
      
      if (scheduledSession) {
        console.log('✅ Custom interview scheduled successfully:', scheduledSession);
        
        // Close the modal
        setShowScheduleModal(false);
        setSelectedInterview(null);
        
        // Show success message
        showToast('Custom interview scheduled successfully!', 'success');
        
        // Refresh upcoming interviews in the sidebar
        if (onRefreshUpcomingInterviews) {
          onRefreshUpcomingInterviews();
        }
        
        // Refresh the custom interviews list to show updated data
        await fetchCustomInterviews();
      } else {
        throw new Error('Failed to schedule custom interview session');
      }
    } catch (error) {
      console.error('Error scheduling custom interview:', error);
      showToast('Failed to schedule custom interview. Please try again.', 'error');
    }
  }, [selectedInterview, onRefreshUpcomingInterviews, showToast, languages]);

  const handleRetakeCustomInterview = async (interview: UserInterview) => {
    try {
      // For retaking, we need to get the custom interview template ID
      // This is stored in interview.interview._id (the template ID)
      const customInterviewTemplateId = interview.interview?._id || '';
      
      navigate(`/interview-preview/${customInterviewTemplateId}`, {
        state: {
          interviewDetails: {
            id: customInterviewTemplateId, // This is the custom interview template ID
                                                title: interview.interview?.title || 'Interview',
                                    company: interview.interview?.company || '',
            jobRole: interview.interview?.role || '',
            duration: interview.interview?.durationMinutes || 30,
            description: interview.interview?.jobDescription || interview.interview?.description || '',
            tags: [],
            status: "not_started",
            language: interview.interview?.language || 'English',
            newInterview: true, // This will trigger session creation in InterviewPreviewContainer
            isCustom: true, // Flag to indicate this is a custom interview
            customInterviewId: customInterviewTemplateId, // Pass the template ID explicitly
            createdAt: interview.createdAt,
            updatedAt: interview.updatedAt,
          }
        },
      });
    } catch (error) {
      console.error('Error retaking custom interview:', error);
      showToast('An error occurred while retaking the interview.', 'error');
    }
  };

  // Fetch custom interviews
  const fetchCustomInterviews = async () => {
    try {
      const interviews = await interviewService.getMyCustomInterviews();
      setCustomInterviews(interviews);
      setCustomInterviewsLoaded(true);
    } catch (error) {
      console.error('Error fetching custom interviews:', error);
      setCustomInterviewsLoaded(true);
    }
  };

  // Load custom interviews when switching to practice tab
  useEffect(() => {
    if (activeTab === 'practice') {
      fetchCustomInterviews();
    } else {
      // Reset the loaded flag when switching away from practice tab
      setCustomInterviewsLoaded(false);
    }
  }, [activeTab]);

  // Reset pagination when custom interviews change
  useEffect(() => {
    setCurrentPage(1);
  }, [customInterviews.length]);

  // Update view based on custom interviews availability
  useEffect(() => {
    if (activeTab === 'practice' && customInterviewsLoaded) {
      // If user has custom interviews, show list by default
      if (customInterviews.length > 0) {
        setShowCustomInterviewsList(true);
        setShowCustomInterviewForm(false);
      } 
      // If no custom interviews, show form by default
      else {
        setShowCustomInterviewForm(true);
        setShowCustomInterviewsList(false);
      }
    }
  }, [activeTab, customInterviews.length, customInterviewsLoaded]);



  // Handle creating a custom practice interview  (not session)
  const handleCreateCustomInterview = async () => {
    if (!isFormComplete) {
      return;
    }

    try {
      setIsCreatingCustomInterview(true);

      // Use custom round type if "Other" is selected, otherwise use selected round type
      const finalRoundType = jobDescription === 'Other' ? customRoundType : jobDescription;

      // Create custom interview template only (not a session)
      const customInterviewTemplate = await interviewService.createCustomInterview({
        jobRole: jobRole,
        company: companyName,
        roundType: finalRoundType,
        jobDescription: actualJobDescription,
        language: interviewLanguage || 'English',
        difficulty: interviewDifficulty, // Add difficulty parameter
        // No scheduledAt here - we're only creating a template
        languageId: interviewLanguage, // Can be added later if needed
      });

      if (customInterviewTemplate) {
         // Fetch updated custom interviews list
        await fetchCustomInterviews();
        showToast('Custom interview created successfully!', 'success');
        // Hide the form and show the list
        setShowCustomInterviewForm(false);
        setShowCustomInterviewsList(true); 
        // Clear form
        setJobRole('');
        setCompanyName('');
        setJobDescription('');
        setCustomRoundType('');
        setActualJobDescription('');
        setInterviewLanguage('English');
        setInterviewDifficulty(3); // Reset difficulty to default
        setAiDescription('');
        setSelectedInterview({
          id: '',
          title: '',
          company: '',
          jobRole: '',
          duration: 30,
          description: '',
          language: 'English',
          score: 0,
          date: new Date().toISOString(),
        });

      }
    } catch (error: any) {
      console.error("❌ Error creating custom interview:", error);
      
      // Check if this is a paywall error
      if (error?.code === 'CUSTOM_INTERVIEW_LIMIT_EXCEEDED') {
        setUsageDetails(error.details);
        setShowPaywallModal(true);
      } else if (error?.response?.data?.error === 'CUSTOM_INTERVIEW_LIMIT_EXCEEDED') {
        const errorDetails = error.response.data;
        setUsageDetails({
          currentMonthCount: errorDetails.currentCount,
          monthlyLimit: errorDetails.monthlyLimit,
          resetDate: errorDetails.resetDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          userPlan: errorDetails.userPlan,
          canCreateMore: false,
          isFreePlan: true
        });
        setShowPaywallModal(true);
      } else {
        showToast(`Failed to create interview. ${(error as any).message[0]}`, 'error');
      }
    } finally {
      setIsCreatingCustomInterview(false);
    }
  };

  // Check if all required fields are filled - simplified for custom interviews
  const isFormComplete = jobRole.trim() !== '' &&
    companyName.trim() !== '' &&
    jobDescription.trim() !== '' &&
    (jobDescription === 'Other' ? customRoundType.trim() !== '' : true) &&
    actualJobDescription.trim() !== '' &&
    interviewLanguage.trim() !== '' &&
    interviewDifficulty >= 1 && interviewDifficulty <= 5;



  // Start async recommendation job and poll for result
  const fetchAsyncRecommendedInterviews = useCallback(async () => {
    // Prevent multiple calls if already loaded
    if (hasLoadedRecommendations.current) {
      console.log('PrepareForInterview: Recommendations already loaded, skipping API call');
      return;
    }

    setIsLoadingRecommendations(true);
    setRecommendationJobStatus('pending');
    setRecommendationJobError(null);
    setRecommendedInterviews([]);
    setFilteredRecommendedInterviews([]);
    
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Reset jobId state to ensure fresh start
    setRecommendationJobId(null);
    
    try {
      console.log('PrepareForInterview: Starting async recommendation job');
      
      // Start the async job
      const { jobId } = await interviewService.startAsyncInterviewRecommendationJob({ userId: user?._id });
      
      // Validate jobId before proceeding
      if (!jobId || jobId === 'undefined' || jobId === 'null') {
        throw new Error('Failed to create recommendation job - invalid jobId received');
      }
      
      setRecommendationJobId(jobId);
      
      // Poll for result with jobId validation
      const poll = async () => {
        try {
          // Double-check jobId is still valid before polling
          if (!jobId || jobId === 'undefined' || jobId === 'null') {
            setRecommendationJobStatus('failed');
            setRecommendationJobError('Invalid job ID - please try again');
            setIsLoadingRecommendations(false);
            return;
          }
          
          const res = await interviewService.pollAsyncInterviewRecommendationJob(jobId);
          console.log('PrepareForInterview: Polling async recommendation job:', res);
          if (res.status === 'pending') {
            setRecommendationJobStatus('pending');
            pollingIntervalRef.current = setTimeout(poll, 2000);
          } else if (res.status === 'success') {
            setRecommendationJobStatus('success');
            
            // Process the results to map metadata properties to top level
            const processedResults = (res.result || []).map(item => ({
              ...item,
              // Extract commonly used properties from metadata to top level
              company: item.metadata?.company || item.company,
              companyLogo: item.metadata?.companyLogo || item.companyLogo,
              companyId: item.metadata?.companyId || item.companyId,
              role: item.metadata?.role || item.role,
              jobRoleId: item.metadata?.jobRoleId || item.jobRoleId,
              jobRoleTitle: item.metadata?.jobRoleTitle || item.jobRoleTitle,
              difficulty: item.metadata?.difficulty || item.difficulty,
              durationMinutes: item.metadata?.durationMinutes || item.durationMinutes,
              tags: item.metadata?.tags,
              features: item.metadata?.features,
              isActive: item.metadata?.isActive !== undefined ? item.metadata.isActive : item.isActive,
              voicePack: item.metadata?.voicePack,
              characterGender: item.metadata?.characterGender
            }));
            
            console.log('PrepareForInterview: Processed async results with metadata mapping:', processedResults.length > 0 ? processedResults[0] : 'none');
            console.log('PrepareForInterview: First processed item companyLogo check:', processedResults.length > 0 ? {
              hasCompanyLogo: !!processedResults[0].companyLogo,
              companyLogoValue: processedResults[0].companyLogo,
              hasMetadataCompanyLogo: !!processedResults[0].metadata?.companyLogo,
              metadataCompanyLogoValue: processedResults[0].metadata?.companyLogo
            } : 'none');
            
            setRecommendedInterviews(processedResults);
            setFilteredRecommendedInterviews(processedResults);
            setIsLoadingRecommendations(false);
            
            // Mark as loaded to prevent future calls
            hasLoadedRecommendations.current = true;
            
            // FIXED: Only trigger callback for onboarding users and only once
            if (onRecommendationLoad && processedResults.length > 0 && !hasTriggeredOnboardingCallback) {
              const isFromOnboarding = localStorage.getItem('from-onboarding') === 'true';
              const previousPath = localStorage.getItem('previous-path');
              
              if (isFromOnboarding || previousPath?.includes('/onboarding/subscription')) {
                console.log('PrepareForInterview: Triggering onboarding callback from async polling');
                // Randomly select an interview from the results from the results length
                const randomIndex = Math.floor(Math.random() * processedResults.length);
                const randomInterview = processedResults[randomIndex];
                if (randomInterview) {
                  console.log('PrepareForInterview: Randomly selected interview:', randomInterview);
                  onRecommendationLoad(randomInterview);
                  setHasTriggeredOnboardingCallback(true);
                }
              }
            }
          } else if (res.status === 'failed' || res.status === 'error') {
            setRecommendationJobStatus('failed');
            setRecommendationJobError(res.error || 'Recommendation job failed');
            setIsLoadingRecommendations(false);
          }
        } catch (err: any) {
          console.error('Polling error:', err);
          setRecommendationJobStatus('failed');
          
          // Provide more specific error messages
          if (err.message?.includes('Invalid jobId')) {
            setRecommendationJobError('Session expired. Please refresh to get new recommendations.');
          } else {
            setRecommendationJobError(err?.message || 'Unknown error occurred');
          }
          setIsLoadingRecommendations(false);
        }
      };
      
      // Start polling
      poll();
    } catch (err: any) {
      console.error('Job creation error:', err);
      setRecommendationJobStatus('failed');
      setRecommendationJobError(err?.message || 'Failed to start recommendation job');
      setIsLoadingRecommendations(false);
    }
  }, [user?._id, onRecommendationLoad, hasTriggeredOnboardingCallback]);

  // FIXED: Only trigger async recommendation polling on initial component mount, not on tab switches
  useEffect(() => {
    // Only fetch recommendations on initial load when user is authenticated
    if (user?._id && isInitialLoad.current) {
      console.log('PrepareForInterview: Initial load, fetching recommendations');
      isInitialLoad.current = false;
      fetchAsyncRecommendedInterviews();
      
      return () => {
        if (pollingIntervalRef.current) {
          clearTimeout(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]); // Only depend on user ID, not on activeTab or fetchAsyncRecommendedInterviews

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Retry handler for failed jobs - always creates a new job
  const handleRetryRecommendationJob = () => {
    console.log('Retrying recommendation job - creating new job');
    hasLoadedRecommendations.current = false; // Reset the flag to allow retry
    fetchAsyncRecommendedInterviews();
  };

  // Handler to generate form data using AI
  const handleGenerateFormData = async () => {
    if (!aiDescription.trim()) {
      showToast('Please enter a description to generate form data', 'error');
      return;
    }

    setIsGeneratingFormData(true);
    
    try {
      console.log('Generating form data with AI for:', aiDescription);
      
      const formData = await interviewService.generateInterviewFormData(aiDescription.trim());
      
      // Populate the form fields with generated data
      setJobRole(formData.jobRole);
      setCompanyName(formData.company);
      setJobDescription(formData.interviewRoundType);
      setActualJobDescription(formData.jobDescription);
      setInterviewLanguage(formData.language);
      setInterviewDifficulty(formData.difficulty == 1 || formData.difficulty == 2 ? 2 : formData.difficulty == 3 ? 3 : 5);
      
      // Clear the AI description input
      setAiDescription('');
      
      showToast('Custom interview details generated successfully!', 'success');
      
    } catch (error: any) {
      console.error('Error generating form data:', error);
      showToast(error.message || 'Failed to generate form data. Please try again.', 'error');
    } finally {
      setIsGeneratingFormData(false);
    }
  };

  // Function to refresh custom interviews and switch to practice tab
  const refreshCustomInterviewsAndSwitchTab = useCallback(() => {
    console.log('🔄 Refreshing custom interviews and switching to practice tab');
    setActiveTab('practice');
    fetchCustomInterviews();
  }, []);

  // Expose the refresh function to parent component
  useEffect(() => {
    if (onRefreshCustomInterviews) {
      // Store the function reference so parent can call it
      (window as any).refreshCustomInterviewsAndSwitchTab = refreshCustomInterviewsAndSwitchTab;
    }
  }, [onRefreshCustomInterviews, refreshCustomInterviewsAndSwitchTab]);

  return (
    <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
      <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
        Prepare For Interviews
      </h2>

      {/* Tabs */}
      <div className="overflow-x-auto md:overflow-visible pb-0 md:mb-0 md:pb-0 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border-light/30 [&::-webkit-scrollbar-thumb]:rounded-full dark:[&::-webkit-scrollbar-thumb]:bg-border-dark/30 hover:[&::-webkit-scrollbar-thumb]:bg-border-light/50 dark:hover:[&::-webkit-scrollbar-thumb]:bg-border-dark/50">
        <div className="inline-flex w-full md:w-auto mb-6 bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm p-1 rounded-lg md:min-w-0">
          <button
            className={`flex-1 md:flex-none px-2 md:px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'recommended'
                ? 'bg-primary-light dark:bg-primary-dark text-white'
                : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
              }`}
            onClick={() => setActiveTab('recommended')}
          >
            <div className="flex items-center justify-center md:justify-start">
              {!isMobileView && <TrendingUp size={16} className="mr-1 md:mr-1.5 flex-shrink-0" />}
              <span className="truncate text-sm">{tabLabels.recommended}</span>
            </div>
          </button>
          <button 
            className={`flex-1 md:flex-none px-2 md:px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              activeTab === 'practice'
                ? 'bg-primary-light dark:bg-primary-dark text-white'
                : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
            }`}
            onClick={() => setActiveTab('practice')}
          >
            <div className="flex items-center justify-center md:justify-start">
              {!isMobileView && <Filter size={16} className="mr-1 md:mr-1.5 flex-shrink-0" />}
              <span className="truncate text-sm">{isMobileView ? 'Custom' : 'Custom Interviews'}</span>
            </div>
          </button>
          { isMobileView && (
            <>
          <button
            className={`flex-1 md:flex-none px-2 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'series'
                ? 'bg-primary-light dark:bg-primary-dark text-white'
                : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
              }`}
            onClick={() => setActiveTab('series')}
          >
            <div className="flex items-center justify-center md:justify-start">
             {!isMobileView && <BookOpen size={16} className="mr-1 md:mr-1.5 flex-shrink-0" />}
              <span className="truncate text-sm">{tabLabels.series}</span>
            </div>
          </button>
          <button
            className={`flex-1 md:flex-none px-2 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 relative ${activeTab === 'feed'
                ? 'bg-primary-light dark:bg-primary-dark text-white'
                : 'text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark'
              }`}
            onClick={() => setActiveTab('feed')}
          >
            <div className="flex items-center justify-center md:justify-start">
              {!isMobileView && <Newspaper size={16} className={`mr-1 md:mr-1.5 flex-shrink-0 ${activeTab == 'feed'? '' : 'text-secondary-light dark:text-secondary-dark'}`} />}
              <span className={`truncate text-sm ${activeTab == 'feed'? '' : 'bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark animate-gradient bg-[length:200%_auto] transition-all duration-1500 ease-out'}`}>{tabLabels.feed}</span>
            </div>
          </button>
          </>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {/* Recommended Interviews - Vertical list layout */}
        {activeTab === 'recommended' && (
          <>
            {/* Search field */}
            <div className="mb-6">
              <div className="flex items-center rounded-lg border border-border-light dark:border-border-dark px-3 py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                <Search size={18} className="text-text-tertiary-light dark:text-text-tertiary-dark" />
                <input
                  type="text"
                  placeholder="Search recommendations by company, role, or keyword..."
                  className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {isSearching ? (
                  <div className="animate-spin w-4 h-4 border-2 border-primary-light border-t-transparent rounded-full"></div>
                ) : searchQuery.trim() != '' ? (
                  <X size={18} className="text-text-secondary-light dark:text-text-secondary-dark" onClick={() => setSearchQuery('')} />
                ) : null}
              </div>
            </div>

            <div className="space-y-4">
              {/* Custom Interview Option Card - Always shown first */}
              <div className="p-4 md:p-5 border-2 border-dashed border-primary-light/40 dark:border-primary-dark/40 rounded-lg bg-gradient-to-br from-primary-light/5 to-secondary-light/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 hover:border-primary-light/60 dark:hover:border-primary-dark/60 transition-all duration-300">
                <div className="flex items-start">
                {!isMobileView && (
                <div className="mr-4 p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex-shrink-0">
                     <Sparkles size={20} className="text-primary-light dark:text-primary-dark" />
                </div>
                )}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-heading font-bold text-lg text-text-primary-light dark:text-text-primary-dark">
                        Create Any Interview Experience
                      </h3>
                      <span className="-mr-1 ml-2 inline-flex items-center text-xs bg-gradient-to-r from-purple-600/15 to-pink-600/15 dark:from-purple-400/15 dark:to-pink-400/15 text-purple-700 dark:text-purple-400 px-2 py-1 rounded-full border border-purple-600/20 dark:border-purple-400/20">
                        <Lightbulb size={12} className="mr-1" />
                        Custom
                      </span>
                    </div>

                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4 leading-relaxed">
                      <span className="font-semibold text-text-primary-light dark:text-text-primary-dark">Dream it. Create it. Practice it.</span> From startup CTO to NASA engineer, Fortune 500 to product based company - create interview experiences for any role, any company, any domain. No limits, no boundaries.
                    </p>

                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center text-xs bg-primary-light/10 dark:bg-primary-dark/10 px-2 py-1 rounded-full border border-primary-light/20 dark:border-primary-dark/20">
                        <Globe size={13} className="mr-1 text-primary-light dark:text-primary-dark" />
                        <span className="text-primary-light dark:text-primary-dark">Any Domain</span>
                      </span>
                      <span className="inline-flex items-center text-xs bg-secondary-light/10 dark:bg-secondary-dark/10 px-2 py-1 rounded-full border border-secondary-light/20 dark:border-secondary-dark/20">
                        <Building size={13} className="mr-1 text-green-600 dark:text-green-400" />
                        <span className="text-green-600 dark:text-green-400">Any Company</span>
                      </span>
                      <span className="inline-flex items-center text-xs bg-purple-500/10 px-2 py-1 rounded-full border border-purple-600/20 dark:border-purple-400/20">
                        <Briefcase size={13} className="mr-1 text-purple-600 dark:text-purple-400" />
                        <span className="text-purple-600 dark:text-purple-400">Any Role</span>
                      </span>
                      <span className="inline-flex items-center text-xs bg-amber-500/10 px-2 py-1 rounded-full border border-amber-600/20 dark:border-amber-400/20">
                        <Zap size={13} className="mr-1 text-amber-600 dark:text-amber-400" />
                        <span className="text-amber-600 dark:text-amber-400">AI-Powered</span>
                      </span>
                    </div>

                    <div className="p-3 rounded-lg mb-4 bg-purple-500/10 dark:bg-purple-400/10 border border-purple-600/20 dark:border-purple-400/20">
                      <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                        <span className="font-semibold font-heading">💡 Pro Tip:</span> Try "AI Engineer at OpenAI", "Product Manager at Netflix", or "Surgeon at Mayo Clinic" - Watch the magic happen!
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="primary"
                        size="sm"
                        className="py-3 px-5"
                        leftIcon={<PlusIcon size={14} />}
                        onClick={handleStartCustomInterview}
                      >
                        Customize Your Interview
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommended Interviews Section */}
              {isLoadingRecommendations ? (
                <div className="text-base flex flex-col items-center justify-center text-center py-12">
                  <div className="animate-spin w-10 h-10 border-4 border-primary-light border-t-transparent rounded-full mb-4"></div>
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">Loading recommendations...</p>
                </div>
              ) : recommendationJobStatus === 'pending' ? (
                <div className="flex flex-col items-center justify-center text-center py-8">
                  <div className="animate-spin w-10 h-10 border-4 border-primary-light border-t-transparent rounded-full mb-4"></div>
                  <div className="text-base text-text-secondary-light dark:text-text-secondary-dark">Generating recommendations...</div>
                </div>
              ) : recommendationJobStatus === 'failed' ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <AlertCircle className="mb-2 text-text-primary-light dark:text-text-primary-dark" size={32} />
                  <p className="text-text-primary-light dark:text-text-primary-dark mb-2 font-heading font-semibold">Failed to generate recommendations</p>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark max-w-lg">
                    {recommendationJobError || 'There was an error generating recommendations. Please try again.'}
                  </p>
                  <Button 
                    className="px-4 py-2 mt-4"
                    variant="outline" 
                    onClick={handleRetryRecommendationJob}>
                      Retry
                    </Button>
                </div>
              ) : (
                <>
                  {/* Regular recommended interviews */}
                  {filteredRecommendedInterviews.length > 0 ? (
                    <>
                      {(() => {
                        // Pagination calculations for recommended interviews
                        const totalRecommendedPages = Math.ceil(filteredRecommendedInterviews.length / recommendedItemsPerPage);
                        const startRecommendedIndex = (currentRecommendedPage - 1) * recommendedItemsPerPage;
                        const endRecommendedIndex = startRecommendedIndex + recommendedItemsPerPage;
                        const currentRecommendedInterviews = filteredRecommendedInterviews.slice(startRecommendedIndex, endRecommendedIndex);
                        console.log('PrepareForInterview: currentRecommendedInterviews:', currentRecommendedInterviews);
                        return (
                          <>
                            {currentRecommendedInterviews.map(interview => (
                  <div key={interview.id} className="p-4 md:p-5 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50 transition-all duration-300">
                    <div className="flex items-start">
                      {!isMobileView && (
                        <div className="mr-4 rounded-full flex-shrink-0">
                          {/* <TrendingUp size={38} className="p-2 text-primary-light dark:text-primary-dark rounded-full bg-background-light dark:bg-background-dark" /> */}
                          <CompanyLogo fallbackIconSize={38} src={interview.companyLogo || interview.metadata.companyLogo || 'N/A'} alt="Logo" className="w-10 h-10 p-1.5 rounded-full object-contain rounded-full bg-background-light dark:bg-background-light" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className={`flex ${isMobileView ? 'flex-col' : 'justify-between items-start'}`}>
                          <div className="flex-1">
                            {isMobileView && (
                              <div className='flex flex-row items-start justify-between'>
                                <div className="mb-2 rounded-full flex-shrink-0">
                                  {/* <TrendingUp size={38} className="p-2 text-primary-light dark:text-primary-dark rounded-full bg-background-light dark:bg-background-dark" /> */}
                                  <CompanyLogo fallbackIconSize={40} src={interview.companyLogo || interview.metadata.companyLogo || 'N/A'} alt="Logo" className="w-11 h-11 p-1.5 rounded-full object-contain rounded-full bg-background-light dark:bg-background-light" />
                                </div>
                                <div className="flex items-center gap-2 -mr-1 mt-0 md:mt-0">
                                      {/* Search result indicator */}
                                      {searchQuery.trim() && interview.metadata?.resultSource && (
                                      <div className="flex items-center gap-2">
                                        {interview.metadata.resultSource === 'text' ? (
                                          <span className="inline-flex items-center text-xs bg-blue-600/15 dark:bg-blue-400/15 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full border border-blue-600/20 dark:border-blue-400/20">
                                            <Search size={10} className="mr-1" />
                                            Text Match
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center text-xs bg-purple-600/15 dark:bg-purple-400/15 text-purple-700 dark:text-purple-400 px-2 py-1 rounded-full border border-purple-600/20 dark:border-purple-400/20">
                                            <Brain size={10} className="mr-1" />
                                            AI Match
                                          </span>
                                        )}
                                        {/* {interview.metadata?.textMatchedFields && interview.metadata.textMatchedFields.length > 0 && (
                                          <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                                            in {interview.metadata.textMatchedFields.join(', ')}
                                          </span>
                                        )} */}
                                    </div>
                                  )}
                                  <span className={`inline-flex items-center text-xs bg-green-600/15 dark:bg-green-400/15 text-green-700 dark:text-green-400 px-2 py-1 rounded-full border border-green-600/20 dark:border-green-400/20`}>
                                    {Math.round((interview.score ?? 0) * 100)}% Match
                                  </span>
                                </div>
                              </div>
                            )}
                            <h3 className="font-heading font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
                              {interview.title || interview.metadata.title || 'Unnamed Interview'}
                            </h3>
                          </div>
                          {!isMobileView && (
                          <div className="flex items-center gap-2 mt-2 md:mt-0">
                               {/* Search result indicator */}
                             {searchQuery.trim() && interview.metadata?.resultSource && (
                              <div className="flex items-center gap-2">
                                {interview.metadata.resultSource === 'text' ? (
                                  <span className="inline-flex items-center text-xs bg-blue-600/15 dark:bg-blue-400/15 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-full border border-blue-600/20 dark:border-blue-400/20">
                                    <Search size={10} className="mr-1" />
                                    Text Match
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center text-xs bg-purple-600/15 dark:bg-purple-400/15 text-purple-700 dark:text-purple-400 px-2 py-1 rounded-full border border-purple-600/20 dark:border-purple-400/20">
                                    <Brain size={10} className="mr-1" />
                                    AI Match
                                  </span>
                                )}
                                {/* {interview.metadata?.textMatchedFields && interview.metadata.textMatchedFields.length > 0 && (
                                  <span className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                                    in {interview.metadata.textMatchedFields.join(', ')}
                                  </span>
                                )} */}
                              </div>
                            )}
                            <span className={`inline-flex items-center text-xs bg-green-600/15 dark:bg-green-400/15 text-green-700 dark:text-green-400 px-2 py-1 rounded-full border border-green-600/20 dark:border-green-400/20`}>
                              {Math.round((interview.score ?? 0) * 100)}% Match
                            </span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-2 mb-3 max-w-2xl">
                          {interview.description || interview.metadata.description || 'No description available.'}
                        </p>

                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          {/* Duration display hidden as it can be confusing for users
                          <DurationDisplay 
                            minutes={interview.metadata.durationMinutes || 30}
                            clockSize={13}
                          /> */}

                        <span className="inline-flex items-center text-xs bg-secondary-light/10 dark:bg-secondary-dark/10 px-2 py-1 rounded-full border border-secondary-light/10 dark:border-secondary-dark/10">
                          <Building size={13} className="mr-1 text-green-600 dark:text-green-400" />
                          <span className="text-green-600 dark:text-green-400">{interview.metadata.company || ''}</span>
                        </span>

                        <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-600/10 dark:border-purple-400/10">
                          <Briefcase size={13} className="mr-1 text-purple-600 dark:text-purple-400" />
                          <span className="text-purple-600 dark:text-purple-400">{interview.metadata.role || ''}</span>
                        </span>

                          <div className={`inline-flex items-center text-xs ${(interview.metadata.difficulty ?? 0) <= 2 ? "bg-green-600/15 dark:bg-green-400/15" :
                              (interview.metadata.difficulty ?? 0) <= 4 ? "bg-amber-600/15 dark:bg-amber-400/15" :
                                "bg-red-600/15 dark:bg-red-400/15"
                            } px-2 py-1 rounded-full border ${(interview.metadata.difficulty ?? 0) <= 2 ? "border-green-600/20 dark:border-green-400/20" :
                              (interview.metadata.difficulty ?? 0) <= 4 ? "border-amber-600/20 dark:border-amber-400/20" :
                                "border-red-600/20 dark:border-red-400/20"
                            }`}>
                            <Brain size={13} className={`mr-1 ${(interview.metadata.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                                (interview.metadata.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                                  "text-red-600 dark:text-red-400"
                              }`} />
                            <span className={`mr-1 ${(interview.metadata.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                                (interview.metadata.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                                  "text-red-600 dark:text-red-400"
                              }`}>Difficulty:</span>
                            <span className={`font-medium ${(interview.metadata.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                                (interview.metadata.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                                  "text-red-600 dark:text-red-400"
                              }`}>
                              {(interview.metadata.difficulty ?? 0) <= 2 ? "Easy" :
                                (interview.metadata.difficulty ?? 0) <= 4 ? "Medium" :
                                  "Hard"}
                            </span>
                          </div>
                        </div>

                        <div className="bg-background-light/50 dark:bg-background-dark/50 p-3 rounded-lg mb-3">
                          <div className="flex items-start">
                            <div className="p-1 mr-2 rounded-full bg-secondary-light/10 dark:bg-secondary-dark/10">
                              <AlertCircle size={14} className="text-secondary-light dark:text-secondary-dark" />
                            </div>
                            <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                              <span className="font-medium">Why this interview:</span> {(interview.metadata.tags || []).map(tag => tag.charAt(0).toUpperCase() + tag.slice(1)).join(', ')}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-4 md:mt-4">
                          <Button
                            variant="primary"
                            size="sm"
                            className='py-3 px-5'
                            leftIcon={<Play size={14} />}
                            onClick={() => handleStartPractice(interview)}
                          >
                            Start Practice
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className='py-2.5 px-5'
                            leftIcon={<Calendar size={14} />}
                            onClick={() => handleScheduleForLater(interview)}
                          >
                            Schedule for Later
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                            ))}
                          </>
                        );
                      })()}
                      
                      {/* Pagination Controls for Recommended Interviews */}
                      {(() => {
                        const totalRecommendedPages = Math.ceil(filteredRecommendedInterviews.length / recommendedItemsPerPage);
                        
                        return totalRecommendedPages > 1 ? (
                          <div className="mt-6 pt-4 border-t border-border-light dark:border-border-dark">
                            {isMobileView ? (
                              // Mobile Layout
                              <div className="space-y-3">
                                {/* Page info - centered on mobile */}
                                <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
                                  Page {currentRecommendedPage} of {totalRecommendedPages}
                                </div>
                                
                                {/* Page numbers row */}
                                <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1">
                                  {generatePageNumbers(currentRecommendedPage, totalRecommendedPages).map((page, index) => {
                                    if (typeof page === 'string') {
                                      // Handle ellipsis
                                      return (
                                        <span
                                          key={page}
                                          className="px-2 py-1 text-text-secondary-light dark:text-text-secondary-dark text-sm flex-shrink-0"
                                        >
                                          ...
                                        </span>
                                      );
                                    }
                                    
                                    // Handle regular page numbers
                                    return (
                                      <Button
                                        key={page}
                                        variant={currentRecommendedPage === page ? "primary" : "outline"}
                                        size="sm"
                                        className={`px-2 py-1 text-sm min-w-[32px] flex-shrink-0 ${currentRecommendedPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                                        onClick={() => {
                                          setCurrentRecommendedPage(page);
                                          window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                      >
                                        {page}
                                      </Button>
                                    );
                                  })}
                                </div>
                                
                                {/* Navigation buttons row */}
                                <div className="flex items-center justify-between gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    leftIcon={<ChevronLeft size={14} />}
                                    disabled={currentRecommendedPage === 1}
                                    onClick={() => {
                                      setCurrentRecommendedPage(prev => Math.max(1, prev - 1));
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    className="flex-1"
                                  >
                                    Prev
                                  </Button>
                                  
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    rightIcon={<ChevronRight size={14} />}
                                    disabled={currentRecommendedPage === totalRecommendedPages}
                                    onClick={() => {
                                      setCurrentRecommendedPage(prev => Math.min(totalRecommendedPages, prev + 1));
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    className="flex-1"
                                  >
                                    Next
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              // Desktop Layout - Original row design
                              <div className="flex items-center justify-between">
                                <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                  Page {currentRecommendedPage} of {totalRecommendedPages}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    leftIcon={<ChevronLeft size={14} />}
                                    disabled={currentRecommendedPage === 1}
                                    onClick={() => {
                                      setCurrentRecommendedPage(prev => Math.max(1, prev - 1));
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                  >
                                    Previous
                                  </Button>
                                  
                                  {/* Page numbers */}
                                  <div className="flex items-center gap-1">
                                    {generatePageNumbers(currentRecommendedPage, totalRecommendedPages).map((page, index) => {
                                      if (typeof page === 'string') {
                                        // Handle ellipsis
                                        return (
                                          <span
                                            key={page}
                                            className="px-3 py-2 text-text-secondary-light dark:text-text-secondary-dark text-sm"
                                          >
                                            ...
                                          </span>
                                        );
                                      }
                                      
                                      // Handle regular page numbers
                                      return (
                                        <Button
                                          key={page}
                                          variant={currentRecommendedPage === page ? "primary" : "outline"}
                                          size="sm"
                                          className={`px-3 ${currentRecommendedPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                                          onClick={() => {
                                            setCurrentRecommendedPage(page);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                          }}
                                        >
                                          {page}
                                        </Button>
                                      );
                                    })}
                                  </div>
                                  
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    rightIcon={<ChevronRight size={14} />}
                                    disabled={currentRecommendedPage === totalRecommendedPages}
                                    onClick={() => {
                                      setCurrentRecommendedPage(prev => Math.min(totalRecommendedPages, prev + 1));
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                  >
                                    Next
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </>
                  ) : (
                    <div className="text-center py-2 text-text-secondary-light dark:text-text-secondary-dark">
                      {searchQuery.trim() ? (
                        <>
                          <p className="text-text-primary-light dark:text-text-primary-dark font-heading font-semibold mb-2">No results found for "{searchQuery}"</p>
                          <p className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark">
                            Try searching for different keywords or browse all interviews
                          </p>
                        </>
                      ) : (
                        <div className="flex flex-col items-center text-center">
                          <Database className="h-12 w-12 text-text-primary-light dark:text-text-primary-dark mb-4" />
                          <p className="text-text-primary-light dark:text-text-primary-dark mb-2 font-heading font-semibold">No recommended interviews available</p>
                          <p className="text-sm text-text-tertiary-light dark:text-text-tertiary-dark max-w-lg">
                            Make sure you have a resume and preferences uploaded to get personalized recommendations.
                            <br /> We are working on adding more interviews to have better recommendations.
                          </p>
                          <div className="mt-4 w-full flex flex-col md:flex-row justify-center gap-2">

                            <Button
                              onClick={() => navigate('/settings-preferences?tab=preferences')}
                              className="px-4 py-2"
                              variant="outline"
                            >
                              Upload Resume
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Browse All Series - With search */}
        {activeTab === 'series' && (
          <>
            {/* Search field */}
            <div className="mb-6">
              <div className="flex items-center rounded-lg border border-border-light dark:border-border-dark px-3 py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                <Search size={18} className="text-text-tertiary-light dark:text-text-tertiary-dark" />
                <input
                  type="text"
                  placeholder="Search by company, job role, or keyword..."
                  className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {isSearching ? (
                  <div className="animate-spin w-4 h-4 border-2 border-primary-light border-t-transparent rounded-full"></div>
                ) : searchQuery.trim() != '' ? (
                  <X size={18} className="text-text-secondary-light dark:text-text-secondary-dark" onClick={() => setSearchQuery('')} />
                ) : null}
              </div>
            </div>

            {/* Series list */}
            <div className="space-y-4">
              {isLoadingSeries || isSearching ? (
                <div className="text-base flex flex-col items-center justify-center py-12">
                  <div className="animate-spin w-10 h-10 border-4 border-primary-light border-t-transparent rounded-full mb-4"></div>
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">{isSearching ? 'Searching for series...' : 'Loading interview series...'}</p>
                </div>
              ) : filteredSeries.length > 0 ? (
                <>
                  {(() => {
                    // Pagination calculations for series
                    const totalSeriesPages = Math.ceil(filteredSeries.length / seriesItemsPerPage);
                    const startSeriesIndex = (currentSeriesPage - 1) * seriesItemsPerPage;
                    const endSeriesIndex = startSeriesIndex + seriesItemsPerPage;
                    const currentSeries = filteredSeries.slice(startSeriesIndex, endSeriesIndex);
                    
                    return (
                      <>
                        {currentSeries.map(series => (
                          <div key={series._id} className="p-4 md:p-5 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50 transition-all duration-300">
                            <div className="flex items-start">
                              {!isMobileView && (
                                <div className="mr-4 rounded-full flex-shrink-0">
                                  {/* <TrendingUp size={18} className="text-primary-light dark:text-primary-dark" /> */}
                                  <CompanyLogo fallbackIconSize={38} src={series.companyLogo || series.companyLogoUrl} alt="Logo" className="w-10 h-10 p-1.5 bg-background-light dark:bg-background-light object-contain rounded-full" />
                                </div>
                              )}

                              <div className="flex-1">
                                <div className={`flex ${isMobileView ? 'flex-col' : 'justify-between items-start'}`}>
                                  {isMobileView && (
                                    <div className='flex flex-row items-start justify-between'>
                                    <div className="mb-2 rounded-full flex-shrink-0">
                                      {/* <TrendingUp size={38} className="p-2 text-primary-light dark:text-primary-dark rounded-full bg-background-light dark:bg-background-dark" /> */}
                                      <CompanyLogo fallbackIconSize={40} src={series.companyLogo || series.companyLogoUrl} alt="Logo" className="w-11 h-11 p-1.5 rounded-full object-contain rounded-full bg-background-light dark:bg-background-light" />
                                    </div>
                                    {Math.round((series.matchScore || series.match_score || 0) * 100) > 0 && (
                                    <div className="flex items-center gap-2 -mr-1 mt-0 md:mt-0">
                                      <span className={`inline-flex items-center text-xs bg-green-600/15 dark:bg-green-400/15 text-green-700 dark:text-green-400 px-2 py-1 rounded-full border border-green-600/20 dark:border-green-400/20`}>
                                        {Math.round((series.matchScore || series.match_score || 0) * 100)}% Match
                                      </span>
                                    </div>
                                    )}
                                  </div>
                                  )}
                                  <h3 className="font-heading font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
                                    {series.name}
                                  </h3>
                                  {!isMobileView && Math.round((series.matchScore || series.match_score || 0) * 100) > 0 && (
                                  <div className={`inline-flex items-center text-xs bg-green-600/15 dark:bg-green-400/15 px-2 py-1 rounded-full border border-green-500/20 dark:border-green-400/20 ${isMobileView ? 'self-start mt-2' : ''}`}>
                                    <span className="mr-1 text-green-700 dark:text-green-400">{Math.round((series.matchScore || series.match_score || 0) * 100)}% Match</span>
                                  </div>
                                  )}
                                </div>

                                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1 mb-3">
                                  {series.description}
                                </p>

                                <div className="flex flex-wrap items-center gap-3 mb-3">
                                  {(series.totalRounds || series.interviews?.length) && (
                                    <span className="inline-flex items-center text-xs bg-blue-600/15 dark:bg-blue-400/15 px-2 py-1 rounded-full border border-blue-600/20 dark:border-blue-400/20">
                                      <Calendar size={13} className="mr-1 text-blue-600 dark:text-blue-400" />
                                      <span className="text-blue-600 dark:text-blue-400">{series.totalRounds || series.interviews?.length} Interview round{series.totalRounds || series.interviews?.length > 1 ? 's' : ''}</span>
                                    </span>
                                  )}

                                {series.company && (
                                  <span className="inline-flex items-center text-xs bg-secondary-light/10 dark:bg-secondary-dark/10 px-2 py-1 rounded-full border border-secondary-light/10 dark:border-secondary-dark/10">
                                    <Building size={13} className="mr-1 text-green-600 dark:text-green-400" />
                                    <span className="text-green-600 dark:text-green-400">{series.company}</span>
                                  </span>
                                  )}
                                  {series.role && (
                                  <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-600/10 dark:border-purple-400/10">
                                    <Briefcase size={13} className="mr-1 text-purple-600 dark:text-purple-400" />
                                    <span className="text-purple-600 dark:text-purple-400">{series.role || series.jobRoleTitle || ''}</span>
                                  </span>
                                  )}
                                  <span className={`inline-flex items-center text-xs ${series.difficulty <= 2 ? "bg-green-600/15 dark:bg-green-400/15" : series.difficulty <= 4 ? "bg-amber-600/15 dark:bg-amber-400/15" : "bg-red-600/15 dark:bg-red-400/15"} px-2 py-1 rounded-full border ${series.difficulty <= 2 ? "border-green-600/20 dark:border-green-400/20" : series.difficulty <= 4 ? "border-amber-600/20 dark:border-amber-400/20" : "border-red-600/20 dark:border-red-400/20"}`}>
                                    <Brain size={13} className={`mr-1 ${series.difficulty <= 2 ? "text-green-600 dark:text-green-400" : series.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`} />
                                    <span className={`mr-1 ${series.difficulty <= 2 ? "text-green-600 dark:text-green-400" : series.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>Difficulty:</span>
                                    <span className={`font-medium ${series.difficulty <= 2 ? "text-green-600 dark:text-green-400" :
                                        series.difficulty <= 4 ? "text-amber-600 dark:text-amber-400" :
                                          "text-red-600 dark:text-red-400"
                                      }`}>
                                      {series.difficulty <= 2 ? "Easy" :
                                        series.difficulty <= 4 ? "Medium" :
                                          "Hard"}
                                    </span>
                                  </span>
                                  {/* <span className="inline-flex items-center text-xs bg-blue-600/15 dark:bg-blue-400/15 px-2 py-1 rounded-full border border-blue-600/20 dark:border-blue-400/20">
                                    <Clock size={13} className="mr-1 text-blue-600 dark:text-blue-400" />
                                    <span className="text-blue-600 dark:text-blue-400">Prep. time: {series.preparationTimeWeeks} {series.preparationTimeWeeks?.includes('week') ? '' : 'weeks'}</span>
                                  </span> */}
                                </div>

                                <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                  <div className="flex items-center">
                                    <User size={15} className="mr-1 text-primary-light dark:text-primary-dark" />
                                    <span>{series.enrollmentCount > 0 ? (
                                      <span><span className='text-blue-600 dark:text-blue-400'>{series.enrollmentCount.toLocaleString()}</span>  <span className='text-text-primary-light dark:text-text-primary-dark'>Prepared</span></span>
                                    ) : (
                                      <span className='text-text-primary-light dark:text-text-primary-dark'>Become the first to prepare</span>
                                    )}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <AwardIcon size={14} className="mr-1 text-green-700 dark:text-green-400" />
                                    <span className='text-green-700 dark:text-green-400'>{series.passScore}% <span className='text-text-primary-light dark:text-text-primary-dark'>Pass score</span></span>
                                  </div>
                                </div>

                                <div className="flex flex-wrap gap-3">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className='py-3 px-5'
                                    onClick={() => navigate(`/interview-series/${series._id}`)}
                                    leftIcon={<Eye size={14} />}
                                  >
                                    View Series
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                  
                  {/* Pagination Controls for Series */}
                  {(() => {
                    const totalSeriesPages = Math.ceil(filteredSeries.length / seriesItemsPerPage);
                    
                    return totalSeriesPages > 1 ? (
                      <div className="mt-6 pt-4 border-t border-border-light dark:border-border-dark">
                        {isMobileView ? (
                          // Mobile Layout
                          <div className="space-y-3">
                            {/* Page info - centered on mobile */}
                            <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
                              Page {currentSeriesPage} of {totalSeriesPages}
                            </div>
                            
                            {/* Page numbers row */}
                            <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1">
                              {generatePageNumbers(currentSeriesPage, totalSeriesPages).map((page, index) => {
                                if (typeof page === 'string') {
                                  // Handle ellipsis
                                  return (
                                    <span
                                      key={page}
                                      className="px-2 py-1 text-text-secondary-light dark:text-text-secondary-dark text-sm flex-shrink-0"
                                    >
                                      ...
                                    </span>
                                  );
                                }
                                
                                // Handle regular page numbers
                                return (
                                  <Button
                                    key={page}
                                    variant={currentSeriesPage === page ? "primary" : "outline"}
                                    size="sm"
                                    className={`px-2 py-1 text-sm min-w-[32px] flex-shrink-0 ${currentSeriesPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                                    onClick={() => {
                                      setCurrentSeriesPage(page);
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                  >
                                    {page}
                                  </Button>
                                );
                              })}
                            </div>
                            
                            {/* Navigation buttons row */}
                            <div className="flex items-center justify-between gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                leftIcon={<ChevronLeft size={14} />}
                                disabled={currentSeriesPage === 1}
                                onClick={() => {
                                  setCurrentSeriesPage(prev => Math.max(1, prev - 1));
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="flex-1"
                              >
                                Prev
                              </Button>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                rightIcon={<ChevronRight size={14} />}
                                disabled={currentSeriesPage === totalSeriesPages}
                                onClick={() => {
                                  setCurrentSeriesPage(prev => Math.min(totalSeriesPages, prev + 1));
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="flex-1"
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // Desktop Layout - Original row design
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                              Page {currentSeriesPage} of {totalSeriesPages}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                leftIcon={<ChevronLeft size={14} />}
                                disabled={currentSeriesPage === 1}
                                onClick={() => {
                                  setCurrentSeriesPage(prev => Math.max(1, prev - 1));
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                              >
                                Previous
                              </Button>
                              
                              {/* Page numbers */}
                              <div className="flex items-center gap-1">
                                {generatePageNumbers(currentSeriesPage, totalSeriesPages).map((page, index) => {
                                  if (typeof page === 'string') {
                                    // Handle ellipsis
                                    return (
                                      <span
                                        key={page}
                                        className="px-3 py-2 text-text-secondary-light dark:text-text-secondary-dark text-sm"
                                      >
                                        ...
                                      </span>
                                    );
                                  }
                                  
                                  // Handle regular page numbers
                                  return (
                                    <Button
                                      key={page}
                                      variant={currentSeriesPage === page ? "primary" : "outline"}
                                      size="sm"
                                      className={`px-3 ${currentSeriesPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                                      onClick={() => {
                                        setCurrentSeriesPage(page);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }}
                                    >
                                      {page}
                                    </Button>
                                  );
                                })}
                              </div>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                rightIcon={<ChevronRight size={14} />}
                                disabled={currentSeriesPage === totalSeriesPages}
                                onClick={() => {
                                  setCurrentSeriesPage(prev => Math.min(totalSeriesPages, prev + 1));
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}
                </>
              ) : (
                <div className="p-2 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <Inbox className="h-12 w-12 text-text-tertiary-light dark:text-text-tertiary-dark" />
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                        No {searchQuery.trim() !== '' ? 'searched' : "recommended" } interview series available
                      </h3>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        {searchQuery.trim() !== '' 
                        ? "It's not on you, it's on us. We're adding more & more series everyday to improve your preparation journey." :
                        'Upload your resume to get personalized interview series recommendations or check back later for new series.'
                      }
                      </p>
                      <div className="mt-4 flex flex-col md:flex-row justify-center gap-2">
                        <Button
                          onClick={() => navigate('/settings-preferences?tab=preferences')}
                          className="px-4 py-2"
                          variant="outline"
                        >
                          Upload Resume
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {/* News For You - Enhanced animated experience */}
        {activeTab === 'feed' && (
          <div className="p-4 md:p-6 border rounded-lg border-border-light dark:border-border-dark bg-gradient-to-br from-background-light/50 to-primary-light/5 dark:from-background-dark/50 dark:to-primary-dark/5 relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute bottom-6 left-6 w-16 h-16 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-full blur-lg animate-bounce"></div>
              <div className="absolute top-1/2 left-1/3 w-12 h-12 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-md animate-float"></div>
            </div>

            <div className="relative z-10">

              {/* How it works animation */}
              <div className="mb-6 p-4 bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg border border-primary-light/20 dark:border-primary-dark/20">
                <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark text-sm mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                  <span className="text-base text-text-primary-light dark:text-text-primary-dark font-heading font-semibold">How it works</span>
                </h4>
                <div className="mt-4 md:mt-0">
                  {isMobileView ? <VerticalProgressFlow /> : <HorizontalProgressFlow />}
                </div>
              </div>

              {/* Call to action */}
              <div className="text-center">
                <div className="mb-4">
                  {/* <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full text-xs font-medium border border-primary-light/20 dark:border-primary-dark/20">
                    <div className="w-2 h-2 bg-primary-light dark:bg-primary-dark rounded-full animate-pulse"></div>
                    <span className="text-primary-light dark:text-primary-dark">Ready to explore</span>
                  </div> */}
                </div>
                <h4 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                  Your personalized news feed awaits!
                </h4>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
                  Get the latest industry insights, trending topics, and skill-specific updates curated just for you.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate('/feed')}
                    variant="primary"
                    size="md"
                    className="text-sm"
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                  >
                    <span className="relative">Explore Your News Feed</span>
                  </Button>
                  <Button
                    onClick={() => navigate('/settings-preferences?tab=preferences')}
                    variant="secondary"
                    size="md"
                    leftIcon={<Settings2 size={16} />}
                    className="text-sm"
                  >
                    Update Preferences
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Custom Interview Loading State */}
        {activeTab === 'practice' && !customInterviewsLoaded && (
          <div className="">
            <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin w-10 h-10 border-4 border-primary-light border-t-transparent rounded-full mb-4"></div>
            <p className="text-text-secondary-light dark:text-text-secondary-dark text-base">Loading your custom interviews...</p>
            </div>
          </div>
        )}
        {/* Custom Interview - Form-based - More flexible for any role/company */}
        {activeTab === 'practice' && customInterviewsLoaded && showCustomInterviewForm && (
          <div className="p-4 md:p-6 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
            <h3 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
              Create Custom Interview
            </h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
              Choose your preferred way to create a custom interview - let AI help you or fill in the details manually.
            </p>

            {/* AI Generation Section */}
            <div className="mb-6 p-4 border-2 border-dashed border-primary-light/40 dark:border-primary-dark/40 rounded-lg bg-gradient-to-br from-primary-light/10 to-primary-light/5 dark:from-primary-dark/10 dark:to-primary-dark/5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10">
                  <Wand2 size={16} className="text-primary-light dark:text-primary-dark" />
                </div>
                <h4 className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                  Generate with AI
                </h4>
              </div>
              
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-3">
                Describe your interview in <span className="text-text-primary-light dark:text-text-primary-dark font-medium">15-20 words</span> and let AI auto-fill the entire form for you.
              </p>
              
              <div className="flex gap-3 flex-col md:flex-row">
                <div className="flex-1">
                  <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                    <input
                      type="text"
                      placeholder="Min. 15-20 words, e.g. HR round for Oracle for sales executive"
                      className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                      value={aiDescription}
                      onChange={(e) => setAiDescription(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !isGeneratingFormData && aiDescription.trim()) {
                          handleGenerateFormData();
                        }
                      }}
                    />
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={isGeneratingFormData ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                  disabled={aiDescription.length < 50 || isGeneratingFormData}
                  onClick={handleGenerateFormData}
                  className="py-3 mb-1 md:py-1 md:mb-0"
                >
                  {isGeneratingFormData ? 'Generating...' : 'Generate'}
                </Button>
              </div>
              
              <div className="mt-3 mb-1 text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                💡 Try: "Machine code round for Google SWE", "HR interview at Microsoft", "Product manager case study at Amazon"
              </div>
            </div>

            {/* Divider with OR */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-light dark:border-border-dark"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 bg-surface-light dark:bg-surface-dark text-sm text-text-tertiary-light dark:text-text-tertiary-dark font-medium">
                  OR
                </span>
              </div>
            </div>

            {/* Manual Form Section */}
            <div className="flex items-center gap-2 mb-4">
              {/* <div className="p-2 rounded-full bg-blue-600/10 dark:bg-blue-400/10">
                <FileText size={16} className="text-blue-600 dark:text-blue-400" />
              </div> */}
              <h4 className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                Enter Manually
              </h4>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <User size={18} className="text-primary-light dark:text-primary-dark" />
                  <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Job Role
                  </label>
                </div>
                <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                  <input
                    type="text"
                    placeholder="e.g. Senior Software Engineer, Product Manager, Data Scientist..."
                    className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                    value={jobRole}
                    onChange={(e) => setJobRole(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Building size={18} className="text-primary-light dark:text-primary-dark" />
                  <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Company Name
                  </label>
                </div>
                <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                  <input
                    type="text"
                    placeholder="e.g. Google, Microsoft, Apple, or any company name..."
                    className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={18} className="text-primary-light dark:text-primary-dark" />
                  <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Interview Round Type
                  </label>
                </div>
                  <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all relative">
                    <select
                      className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
                      value={jobDescription}
                      onChange={(e) => {
                        setJobDescription(e.target.value);
                        if (e.target.value !== 'Other') {
                          setCustomRoundType(''); // Clear custom input when selecting predefined option
                        }
                      }}
                    >
                    <option value="">Select round type</option>
                    <option value="Technical Round">Technical Round</option>
                    <option value="Behavioral Round">Behavioral Round</option>
                    <option value="System Design Round">System Design Round</option>
                    <option value="Coding Round">Coding Round</option>
                    <option value="Case Study Round">Case Study Round</option>
                    <option value="Product Management Round">Product Management Round</option>
                    <option value="Leadership Round">Leadership Round</option>
                    <option value="Final Round">Final Round</option>
                    <option value="HR Round">HR Round</option>
                    <option value="General Interview">General Interview</option>
                    <option value="Other">Other (Custom)</option>
                  </select>
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronRight size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark transform rotate-90" />
                  </div>
                </div>
                
                {/* Custom round type input - shown when "Other" is selected */}
                {jobDescription === 'Other' && (
                  <div className="mt-3">
                    <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                      <input
                        type="text"
                        placeholder="e.g. AI/ML Round, DevOps Round, Sales Pitch, Design Critique..."
                        className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                        value={customRoundType}
                        onChange={(e) => setCustomRoundType(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Brain size={18} className="text-primary-light dark:text-primary-dark" />
                  <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Interview Difficulty
                  </label>
                </div>
                <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all relative">
                <select
                  value={interviewDifficulty}
                  onChange={(e) => setInterviewDifficulty(Number(e.target.value))}
                  className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
                >
                  <option value={2}>Easy - For getting started, short practice rounds</option>
                  <option value={3}>Medium - For some challenges, mid-level duration rounds</option>
                  <option value={5}>Hard - For hands on, tricky & complex rounds</option>
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronRight size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark transform rotate-90" />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={18} className="text-primary-light dark:text-primary-dark" />
                  <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Job Description
                  </label>
                </div>
                <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                  <textarea
                    placeholder="Paste the complete job description here, or describe the key responsibilities and requirements for this role..."
                    rows={6}
                    className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm resize-vertical"
                    value={actualJobDescription}
                    onChange={(e) => setActualJobDescription(e.target.value)}
                  ></textarea>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Globe size={18} className="text-primary-light dark:text-primary-dark" />
                  <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Interview Language
                  </label>
                </div>
                <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all relative">
                <select
                  value={interviewLanguage}
                  onChange={(e) => setInterviewLanguage(e.target.value)}
                  className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
                >
                  {languages.length > 0 ? (
                    languages.map(language => (
                      <option key={language._id} value={language.name}>
                        {language.name}
                      </option>
                    ))
                  ) : (
                    // Fallback options if API fails
                    <>
                      <option>No Language Selected</option>
                    </>
                  )}
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronRight size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark transform rotate-90" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="primary"
                size="md"
                leftIcon={isCreatingCustomInterview ? <Loader2 size={16} className="animate-spin" /> : <PlusIcon size={16} />}
                disabled={!isFormComplete || isCreatingCustomInterview}
                onClick={handleCreateCustomInterview}
              >
                {isCreatingCustomInterview ? 'Creating...' : 'Create Interview'}
              </Button>
              
              {customInterviewsLoaded && customInterviews.length > 0 && !showCustomInterviewsList && showCustomInterviewForm && (
                <Button
                  variant="secondary"
                  size="md"
                  leftIcon={<Eye size={16} />}
                  onClick={() => {
                    setShowCustomInterviewForm(false);
                    setShowCustomInterviewsList(true);
                  }}
                >
                  View My Interviews ({customInterviews.length})
                </Button>
              )}
              

            </div>
          </div>
        )}

        {/* Custom Interviews List */}
        {activeTab === 'practice' && customInterviewsLoaded && showCustomInterviewsList && customInterviews.length > 0 && (
          (() => {
            // Pagination calculations
            const totalPages = Math.ceil(customInterviews.length / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const currentInterviews = customInterviews.slice(startIndex, endIndex);
            
            return (
              <div>
                <div className="space-y-6">
                 <div className="space-y-4"> 
                  {currentInterviews.map((interview) => (
                <div key={interview._id} className="p-4 md:p-5 border rounded-lg border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark hover:border-primary-light/50 dark:hover:border-primary-dark/50 transition-all duration-300">
                  <div className="flex items-start">
                    {!isMobileView && (
                      <div className="mr-4 p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex-shrink-0">
                        <TestTube2Icon size={18} className="text-primary-light dark:text-primary-dark" />
                      </div>
                    )}

                    <div className="flex-1">
                      <div className={`flex ${isMobileView ? 'flex-col' : 'justify-between items-start'}`}>
                        <h3 className="font-heading font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
                          {interview.interview?.title || 'Custom Interview'}
                        </h3>
                      </div>

                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-2 mb-3">
                        {interview.interview?.jobDescription?.split('.')[0] + '..' || interview.interview?.description}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        {/* Duration display hidden as it can be confusing for users
                        <DurationDisplay 
                          minutes={interview.interview?.durationMinutes || 30}
                          clockSize={13}
                        /> */}

                        <span className="inline-flex items-center text-xs bg-secondary-light/10 dark:bg-secondary-dark/10 px-2 py-1 rounded-full border border-secondary-light/10 dark:border-secondary-dark/10">
                          <Building size={13} className="mr-1 text-green-600 dark:text-green-400" />
                          <span className="text-green-600 dark:text-green-400">{interview.interview?.company || ''}</span>
                        </span>

                        <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-600/10 dark:border-purple-400/10">
                          <Briefcase size={13} className="mr-1 text-purple-600 dark:text-purple-400" />
                          <span className="text-purple-600 dark:text-purple-400">{interview.interview?.role || ''}</span>
                        </span>

                        <div className={`inline-flex items-center text-xs ${(interview.interview?.difficulty ?? 0) <= 2 ? "bg-green-600/15 dark:bg-green-400/15" :
                              (interview.interview?.difficulty ?? 0) <= 4 ? "bg-amber-600/15 dark:bg-amber-400/15" :
                                "bg-red-600/15 dark:bg-red-400/15"
                            } px-2 py-1 rounded-full border ${(interview.interview?.difficulty ?? 0) <= 2 ? "border-green-600/20 dark:border-green-400/20" :
                              (interview.interview?.difficulty ?? 0) <= 4 ? "border-amber-600/20 dark:border-amber-400/20" :
                                "border-red-600/20 dark:border-red-400/20"
                            }`}>
                            <Brain size={13} className={`mr-1 ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                                (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                                  "text-red-600 dark:text-red-400"
                              }`} />
                            <span className={`mr-1 ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                                (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                                  "text-red-600 dark:text-red-400"
                              }`}>Difficulty:</span>
                            <span className={`font-medium ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                                (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                                  "text-red-600 dark:text-red-400"
                              }`}>
                              {(interview.interview?.difficulty ?? 0) <= 2 ? "Easy" :
                                (interview.interview?.difficulty ?? 0) <= 4 ? "Medium" :
                                  "Hard"}
                            </span>
                        </div>
                          
                      </div>

                      {/* <div className="bg-background-light/50 dark:bg-background-dark/50 p-3 rounded-lg mb-3">
                          <div className="flex items-start">
                            <div className="p-1 mr-2 rounded-full bg-secondary-light/10 dark:bg-secondary-dark/10">
                              <AlertCircle size={14} className="text-secondary-light dark:text-secondary-dark" />
                            </div>
                            <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                              <span className="font-medium">Why this interview:</span> {(interview.interview?.features || []).map(feature => feature.charAt(0).toUpperCase() + feature.slice(1)).join(', ')}
                            </span>
                          </div>
                      </div> */}

                      <div className="flex flex-wrap gap-3 mt-4 md:mt-4">
                        <Button
                          variant="primary"
                          size="sm"
                          className='px-5'
                          leftIcon={<Play size={14} />}
                          onClick={() => {
                            if (interview.status === 'completed') {
                              handleRetakeCustomInterview(interview);
                            } else {
                              // For starting a new practice session, use the custom interview template ID
                              const customInterviewTemplateId = interview.interview?._id;
                              
                              navigate(`/interview-preview/${customInterviewTemplateId}`, {
                                state: {
                                  interviewDetails: {
                                    id: customInterviewTemplateId, // This is the custom interview template ID
                                    title: interview.interview?.title || 'Interview',
                                    company: interview.interview?.company || '',
                                    companyLogo: null, // Not available in custom interviews
                                    jobRole: interview.interview?.role || '',
                                    duration: interview.interview?.durationMinutes || 30,
                                    description: interview.interview?.jobDescription || 'Interview description',
                                    tags: [],
                                    status: "not_started",
                                    language: interview.interview?.language || 'English',
                                    newInterview: true, // This will trigger session creation in InterviewPreviewContainer
                                    isCustom: true, // Flag to indicate this is a custom interview
                                    customInterviewId: customInterviewTemplateId, // Pass the template ID explicitly
                                    voicePack: undefined,
                                    characterGender: undefined,
                                    interviewPrompt: interview.interview?.interviewPrompt || undefined,
                                    series: undefined,
                                    createdAt: interview.createdAt,
                                    updatedAt: interview.updatedAt,
                                  }
                                },
                              });
                            }
                          }}
                        >
                          {interview.status === 'completed' ? 'Retake Practice' : 'Start Practice'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className='py-2.5 px-5'
                          leftIcon={<Calendar size={14} />}
                          onClick={() => handleScheduleCustomForLater(interview)}
                        >
                          Schedule for Later
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div> 
              <div className="flex justify-center flex-col md:flex-row gap-3">
              <Button
                variant="primary"
                size="md"
                leftIcon={<PlusIcon size={16} />}
                onClick={() => {
                  setShowCustomInterviewForm(true);
                  setShowCustomInterviewsList(false);
                }}
              >
                Create More Interviews
              </Button>
              <Button
                variant="secondary"
                size="md"
                leftIcon={<HistoryIcon size={16} />}
                onClick={() => {
                  navigate('/my-interviews?tab=history');
                }}
              >
                View Completed Interviews
              </Button>
              </div>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 pt-4 border-t border-border-light dark:border-border-dark">
                {isMobileView ? (
                  // Mobile Layout
                  <div className="space-y-3">
                    {/* Page info - centered on mobile */}
                    <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
                      Page {currentPage} of {totalPages}
                    </div>
                    
                    {/* Page numbers row */}
                    <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1">
                      {generatePageNumbers(currentPage, totalPages).map((page, index) => {
                        if (typeof page === 'string') {
                          // Handle ellipsis
                          return (
                            <span
                              key={page}
                              className="px-2 py-1 text-text-secondary-light dark:text-text-secondary-dark text-sm flex-shrink-0"
                            >
                              ...
                            </span>
                          );
                        }
                        
                        // Handle regular page numbers
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "primary" : "outline"}
                            size="sm"
                            className={`px-2 py-1 text-sm min-w-[32px] flex-shrink-0 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                            onClick={() => {
                              setCurrentPage(page);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    
                    {/* Navigation buttons row */}
                    <div className="flex items-center justify-between gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ChevronLeft size={14} />}
                        disabled={currentPage === 1}
                        onClick={() => {
                          setCurrentPage(prev => Math.max(1, prev - 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="flex-1"
                      >
                        Prev
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<ChevronRight size={14} />}
                        disabled={currentPage === totalPages}
                        onClick={() => {
                          setCurrentPage(prev => Math.min(totalPages, prev + 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="flex-1"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Desktop Layout - Original row design
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<ChevronLeft size={14} />}
                        disabled={currentPage === 1}
                        onClick={() => {
                          setCurrentPage(prev => Math.max(1, prev - 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Previous
                      </Button>
                      
                      {/* Page numbers */}
                      <div className="flex items-center gap-1">
                        {generatePageNumbers(currentPage, totalPages).map((page, index) => {
                          if (typeof page === 'string') {
                            // Handle ellipsis
                            return (
                              <span
                                key={page}
                                className="px-3 py-2 text-text-secondary-light dark:text-text-secondary-dark text-sm"
                              >
                                ...
                              </span>
                            );
                          }
                          
                          // Handle regular page numbers
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? "primary" : "outline"}
                              size="sm"
                              className={`px-3 ${currentPage === page ? '' : 'text-text-secondary-light dark:text-text-secondary-dark'}`}
                              onClick={() => {
                                setCurrentPage(page);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                            >
                              {page}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<ChevronRight size={14} />}
                        disabled={currentPage === totalPages}
                        onClick={() => {
                          setCurrentPage(prev => Math.min(totalPages, prev + 1));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
              </div>
            );
          })()
        )}
        
        {showScheduleModal && selectedInterview && (
          <ScheduleInterviewModal
            interviewDetails={selectedInterview}
            isOpen={showScheduleModal}
            onClose={() => setShowScheduleModal(false)}
            onSchedule={handleScheduleCustomSubmit}
          />
        )}

        {/* Schedule Modal for Recommended Interviews */}
        {showScheduleModalForRecommended && selectedInterviewForScheduling && (
          <ScheduleInterviewModal
            interviewDetails={{
              id: selectedInterviewForScheduling.id,
              title: selectedInterviewForScheduling.title || selectedInterviewForScheduling.metadata.title,
              company: selectedInterviewForScheduling.metadata.company,
              jobRole: selectedInterviewForScheduling.metadata.role,
              duration: selectedInterviewForScheduling.metadata.durationMinutes,
              description: selectedInterviewForScheduling.description || selectedInterviewForScheduling.metadata.description,
              language: 'English'
            }}
            isOpen={showScheduleModalForRecommended}
            onClose={() => {
              setShowScheduleModalForRecommended(false);
              setSelectedInterviewForScheduling(null);
            }}
            onSchedule={handleScheduleSubmit}
          />
        )}

        {/* Custom Interview Paywall Modal */}
        {showPaywallModal && usageDetails && (
          <CustomInterviewPaywallModal
            isOpen={showPaywallModal}
            onClose={() => setShowPaywallModal(false)}
            onUpgrade={() => {
              setShowPaywallModal(false);
              // Navigate to subscription/pricing page
              navigate('/subscription');
            }}
            usageDetails={usageDetails}
          />
        )}
      </div>
    </div>
  );
};